import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ACCOUNT_MANAGEMENT } from "@consts";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { wrapAccountRow, unwrapFieldValues } from "../AccountEditor.utils";
import useAppStore from "src/stores/appStore";
import {
  editAccountSelector,
  getAccountsAdminSelector,
  managementAccountsSelector,
  accountManagementErrorSelector,
  accountManagementErrorTextSelector,
  setAccountManagementErrorSelector
} from "src/stores/selectors/appSelectors";
import { useFetchAccountSettings, useEditAccountSettings } from "src/queries/hooks/accountSettings";

const useAccountSettings = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { accountRowId, accountId } = useParams();
  const isAccountContext = !accountRowId && !!accountId;
  const [searchParams] = useSearchParams();

  const managementAccounts = useAppStore(managementAccountsSelector);
  const getAccounts = useAppStore(getAccountsAdminSelector);
  const editAccountAdmin = useAppStore(editAccountSelector);
  const error = useAppStore(accountManagementErrorSelector);
  const errorText = useAppStore(accountManagementErrorTextSelector);
  const setError = useAppStore(setAccountManagementErrorSelector);

  const { data: accountSettingsData, isLoading: isLoadingAccountSettings } = useFetchAccountSettings({
    accountId,
    enabled: isAccountContext
  });

  const editAccountSettingsMutation = useEditAccountSettings({
    onSuccess: () => {
      setIsSaving(false);
      setIsConfirmationOpen(false);
      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.ACCOUNT_SETTINGS, accountId] });
    },
    onError: () => {
      setIsSaving(false);
      setIsConfirmationOpen(false);
    }
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  useEffect(() => {
    if (!isAccountContext && !managementAccounts) {
      getAccounts();
    }
  }, [isAccountContext, managementAccounts, getAccounts]);

  useEffect(() => {
    if (!isAccountContext && isSaving && !error) {
      setIsSaving(false);
      setIsConfirmationOpen(false);
    }
  }, [isAccountContext, managementAccounts, error]);

  useEffect(() => {
    if (!isAccountContext && error) {
      setIsSaving(false);
      setIsConfirmationOpen(false);
    }
  }, [isAccountContext, error]);

  const accountRow = isAccountContext
    ? accountSettingsData
    : managementAccounts?.find(account => account.id === accountRowId);
  const accountData = accountRow ? wrapAccountRow(accountRow, "edit") : null;
  const isLoading = isAccountContext ? isLoadingAccountSettings : !managementAccounts;

  const handleSubmit = (data) => {
    const flat = unwrapFieldValues(data);
    setIsSaving(true);

    if (isAccountContext) {
      editAccountSettingsMutation.mutate({
        accountId,
        data: {
          accountName: flat.name,
          accountId: flat.accountId,
          timezone: flat.timezone,
          sftpIpList: flat.sftpIpList,
          publicKey: flat.publicKey
        }
      });
    } else {
      editAccountAdmin({
        id: data.id,
        name: flat.name,
        accountId: flat.accountId,
        timezone: flat.timezone,
        sftpIpList: flat.sftpIpList,
        publicKey: flat.publicKey
      });
    }
  };

  const handleDiscard = () => {
    setIsConfirmationOpen(false);
    navigate(isAccountContext ? `/${accountId}` : `/admin/${ACCOUNT_MANAGEMENT}`, { replace: true });
  };

  return {
    managementAccounts: isAccountContext ? (accountSettingsData ? [accountSettingsData] : null) : managementAccounts,
    accountRow,
    accountData,
    isAccountContext,
    isLoading,
    isSaving,
    isConfirmationOpen,
    error: isAccountContext ? editAccountSettingsMutation.isError : error,
    errorText: isAccountContext
      ? (editAccountSettingsMutation.error?.response?.data?.message || (editAccountSettingsMutation.isError ? "Something went wrong" : ""))
      : errorText,
    setError: isAccountContext ? () => editAccountSettingsMutation.reset() : setError,
    searchParams,
    handleSubmit,
    handleDiscard,
    openConfirmation: () => setIsConfirmationOpen(true),
    closeConfirmation: () => setIsConfirmationOpen(false)
  };
};

export default useAccountSettings;
